/**
 * useConfirm — composable Promise-based para modales de confirmación.
 *
 * Reemplaza window.confirm() con un modal integrado al design system.
 * Usa el <ConfirmModal /> existente, montado globalmente en
 * <GlobalOverlays /> dentro de app.vue.
 *
 * Uso simple yes/no (95% de casos):
 *   const { confirmed } = useConfirm()
 *   if (!await confirmed({ message: '¿Eliminar?', variant: 'danger', confirmLabel: 'Eliminar' })) return
 *   await store.remove(id)
 *
 * Uso con 3 botones (confirm / secondary / cancel):
 *   const { confirm } = useConfirm()
 *   const result = await confirm({
 *     message: '...',
 *     confirmLabel: 'Reemplazar',
 *     secondaryAction: { label: 'Agregar al final', variant: 'secondary' },
 *   })
 *   if (result === 'confirm') doReplace()
 *   else if (result === 'secondary') doAppend()
 *
 * Concurrencia: si hay un confirm abierto y se llama otro, el anterior
 * se resuelve automáticamente con 'cancel'.
 */
import { useState } from '#imports'

export interface ConfirmOptions {
  title?: string
  message: string
  confirmLabel?: string
  cancelLabel?: string
  variant?: 'primary' | 'danger'
  secondaryAction?: { label: string; variant?: 'primary' | 'secondary' }
  tertiaryAction?:  { label: string; variant?: 'primary' | 'secondary' }
}

export type ConfirmResult = 'confirm' | 'cancel' | 'secondary' | 'tertiary'

interface ConfirmState {
  open: boolean
  options: ConfirmOptions | null
  resolve: ((r: ConfirmResult) => void) | null
}

function initialState(): ConfirmState {
  return { open: false, options: null, resolve: null }
}

/**
 * Helper interno usado por <GlobalOverlays /> para leer el estado.
 * Prefijo `_` indica uso interno — los callers deben usar useConfirm().
 */
export function _useConfirmState() {
  return useState<ConfirmState>('app-confirm', initialState)
}

/**
 * Helper interno usado por <GlobalOverlays /> para resolver el confirm
 * actual al recibir un evento del ConfirmModal.
 */
export function _resolveConfirm(result: ConfirmResult): void {
  const state = _useConfirmState()
  state.value.resolve?.(result)
  state.value.open = false
  state.value.options = null
  state.value.resolve = null
}

export function useConfirm() {
  const state = _useConfirmState()

  function confirm(options: ConfirmOptions): Promise<ConfirmResult> {
    return new Promise<ConfirmResult>((resolve) => {
      // Concurrencia: auto-cancelar el confirm anterior si está abierto
      if (state.value.open && state.value.resolve) {
        state.value.resolve('cancel')
      }
      state.value.options = options
      state.value.resolve = resolve
      state.value.open = true
    })
  }

  async function confirmed(options: ConfirmOptions): Promise<boolean> {
    return (await confirm(options)) === 'confirm'
  }

  return { confirm, confirmed }
}
